// import { setLocal } from '@utils';
import React from 'react';
import { AccountsService } from '@services';
import { IAccount, IAccountProject } from '@models';
import { useDispatch, useSelector } from 'react-redux';
import { ProjectPickerActions, IApplicationState } from '@store';
import { ProjectPickerHelper } from '@utils';

export enum ControlState {
  NOT_CONFIGURED = 'NOT_CONFIGURED',
  INIT = 'INIT',
}

export const useAccountsService = () => {
  const dispatch = useDispatch();

  const [lastUsedAccountId, setLastUsedAccountId] =
    React.useState<string | null>(null);

  const [controlState, setControlInit] = React.useState<ControlState>(
    ControlState.NOT_CONFIGURED
  );

  // User's Role - @Store --> Auththentication --> AdaptiveSuperUser
  const isAdaptiveAdmin = useSelector(
    (state: IApplicationState) => state.authentication.user?.adaptiveSuperUser
  );

  // AccountId - @Store --> ProjectPicker --> AccountId
  const isActiveAccountId = useSelector(
    (state: IApplicationState) => state.projectPicker.activeAccountId
  );

  // User's LastUsed Project - @Store -> ProjectPicker --> ProjectId
  const isActiveProjectId = useSelector(
    (state: IApplicationState) => state.projectPicker.activeProjectId
  );

  // Project Pickers' - @Store --> ProjectPicker --> Project
  const accountsList = useSelector(
    (state: IApplicationState) => state.projectPicker.accountsList
  );

  React.useEffect(() => {
    setLastUsedAccountId(isActiveAccountId);
  }, [isActiveAccountId, isActiveProjectId]);

  // INIT
  React.useEffect(() => {
    // manual control - to ensure user isAuthenticated (see app.tsx where start() is called)
    if (controlState === ControlState.INIT) {
      if (isAdaptiveAdmin && accountsList === null) {
        configureAccountsList();
      }
      // IF - activeProject & activeAccountId aren't set then most likely it won't find the lastUsedProject in localStorage
      if (lastUsedAccountId) {
        try {
          // TRY - and find it in localStorage (should be there...)
          const lastUsedProject =
            ProjectPickerHelper.findLastUsedProject(lastUsedAccountId);
          // found lastUsedProject - now configure the projectsList...
          configureProjectsList(lastUsedProject);
        } catch (e) {
          // cannot find the lastUsedproject...
          configureProjectsList();
        }
      } else {
        // cannot find the lastUsedproject...
        configureProjectsList();
      }
    }
  }, [controlState, lastUsedAccountId]);

  const configureAccountsList = async () => {
    const accountsList = await getAdminAccountsList();
    dispatch(ProjectPickerActions.updateAccountsList(accountsList));
  };

  const configureProjectsList = async (lastUsedProject?: IAccountProject) => {
    let projectsList: IAccountProject[];
    dispatch(ProjectPickerActions.requestProjectsList());
    // IF - last used project is provided
    if (lastUsedProject) {
      if (isAdaptiveAdmin) {
        projectsList = await getProjectsByAccountId(lastUsedProject.accountId);
        dispatch(ProjectPickerActions.updateProjectPicker(lastUsedProject));
        dispatch(ProjectPickerActions.updateProjectsList(projectsList));
        setLastUsedAccountId(lastUsedProject.accountId);
      } else {
        projectsList = await getProjects();
        dispatch(ProjectPickerActions.updateProjectPicker(lastUsedProject));
        dispatch(ProjectPickerActions.updateProjectsList(projectsList));
        setLastUsedAccountId(lastUsedProject.accountId);
      }
    } else {
      // IF - last used project not found
      if (isAdaptiveAdmin && accountsList) {
        if (lastUsedAccountId) {
          projectsList = await getProjectsByAccountId(lastUsedAccountId);
          dispatch(ProjectPickerActions.updateProjectPicker(projectsList[0]));
          dispatch(ProjectPickerActions.updateProjectsList(projectsList));
          setLastUsedAccountId(projectsList[0].accountId);
        }
      } else {
        projectsList = await getProjects();
        dispatch(ProjectPickerActions.updateProjectPicker(projectsList[0]));
        dispatch(ProjectPickerActions.updateProjectsList(projectsList));
        setLastUsedAccountId(projectsList[0].accountId);
      }
    }
  };

  const getAdminAccountsList = async (): Promise<IAccount[]> => {
    const accounts = await AccountsService.getUserAccountsV2();
    return accounts.length ? accounts : [];
  };

  // GET - Projects for AdaptiveAdmin (AdaptiveAdmin user's must supply accountId)
  const getProjectsByAccountId = async (
    accountId: string
  ): Promise<IAccountProject[]> => {
    const response = await AccountsService.getProjectsByAccountId(accountId);
    return response;
  };

  const getProjects = async (): Promise<IAccountProject[]> => {
    const response = await AccountsService.getProjects();
    return response;
  };

  const _start = async () => {
    setControlInit(ControlState.INIT);
  };

  return {
    start: _start,
  };
};
